import { useState, useEffect, useRef } from "react";
import parse, { domToReact } from "html-react-parser";
import { FaPrint, FaSave, FaPhoneAlt, FaGlobe, FaMapMarkerAlt, FaEnvelope, FaTrash, FaPlus } from "react-icons/fa";
import hospitalLogoWhite from '../../assets/logo/mukti-logo-white.png'; 

const LabEditor = ({ template, patient, doctor, onSave }) => {
  const [formData, setFormData] = useState({});
  const [editableContent, setEditableContent] = useState("");
  const componentRef = useRef();

  // Initialize Data
  useEffect(() => {
    let processedContent = template?.content || "";
    let docDoctorName = "";

    if (processedContent) {
        const refMatch = processedContent.match(/(Referred By[\s\S]*?)__INPUT:(.*?)__/i);
        
        if (refMatch && refMatch[2]) {
            docDoctorName = refMatch[2].trim();
            processedContent = processedContent.replace(refMatch[0], `${refMatch[1]}{{Ref}}`);
        }
        
        setEditableContent(processedContent);
    }

    if (patient) {

      let finalReferredBy = "";

      if (doctor) {
          finalReferredBy = doctor.translations?.en?.name || doctor.name || "";
      } else if (docDoctorName) {
          finalReferredBy = docDoctorName;
      } else {
          finalReferredBy = patient.referredBy || "";
      }

      setFormData({
        name: patient.name || "",
        age: patient.age || "",
        reg: patient.id || "",
        date: new Date().toLocaleDateString("en-GB"),
        referredBy: finalReferredBy, 
      });
    }
  }, [patient, template, doctor]);

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // --- 💾 SAVE LOGIC (THE FIX) ---
  const handleFinalSave = () => {
    if (!componentRef.current) return;

    // 1. Clone the current view so we don't mess up the editor
    const clone = componentRef.current.cloneNode(true);

    // 2. REPLACE INPUTS WITH TEXT
    const inputs = clone.querySelectorAll('input');
    inputs.forEach(input => {
        const span = document.createElement('span');
        span.textContent = input.value;
        span.className = input.className;
        span.style.borderBottom = "none";
        span.style.background = "transparent";
        input.parentNode.replaceChild(span, input);
    });

    // 3. REMOVE BUTTONS
    const buttons = clone.querySelectorAll('button, .delete-cell, .add-row-btn');
    buttons.forEach(btn => btn.remove());

    let finalHtml = clone.innerHTML;

    // Replace Header placeholders
    finalHtml = finalHtml.replace(/{{Date}}/g, formData.date || "");
    finalHtml = finalHtml.replace(/{{Name}}/g, formData.name || "");
    finalHtml = finalHtml.replace(/{{Age}}/g, formData.age || "");
    finalHtml = finalHtml.replace(/{{Reg}}/g, formData.reg || "");
    finalHtml = finalHtml.replace(/{{Ref}}/g, formData.referredBy || "");

    finalHtml = finalHtml.replace(/__INPUT:(.*?)__/g, '$1'); 
    finalHtml = finalHtml.replace(/__INPUT__/g, '');

    // 5. Send the CLEAN HTML to Parent
    onSave(finalHtml, formData);
  };

  // --- DYNAMIC FUNCTIONS (TABLES) ---
  const addTestRow = (tableIndex) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editableContent, "text/html");
    const tables = doc.querySelectorAll('table.dynamic-result-table');
    const table = tables[tableIndex];
    if (table) {
        const tbody = table.querySelector('tbody') || table;
        const lastRow = tbody.lastElementChild;
        if (lastRow) {
            const newRow = lastRow.cloneNode(true);
            newRow.innerHTML = newRow.innerHTML.replace(/value=".*?"/g, '').replace(/__INPUT:[^<]*?__/g, '__INPUT__');
            tbody.appendChild(newRow);
            setEditableContent(doc.body.innerHTML);
        }
    }
  };

  const deleteTestRow = (tableIndex, rowIndex) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editableContent, "text/html");
    const tables = doc.querySelectorAll('table.dynamic-result-table');
    const table = tables[tableIndex];
    if (table) {
        const tbody = table.querySelector('tbody') || table;
        const rows = tbody.querySelectorAll('tr'); 
        if (rows.length > 2 && rowIndex > 0 && rows[rowIndex]) {
            rows[rowIndex].remove();
            setEditableContent(doc.body.innerHTML);
        } else {
            alert("Cannot delete the header.");
        }
    }
  };

  // --- DYNAMIC FUNCTIONS (GRIDS) ---
  const addGridRow = (index) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editableContent, "text/html");
    const grids = doc.querySelectorAll('.dynamic-grid-row');
    const currentGrid = grids[index];
    if (currentGrid) {
        const newGrid = currentGrid.cloneNode(true);
        newGrid.innerHTML = newGrid.innerHTML.replace(/value=".*?"/g, '').replace(/__INPUT:[^<]*?__/g, '__INPUT__');
        currentGrid.parentNode.insertBefore(newGrid, currentGrid.nextSibling);
        setEditableContent(doc.body.innerHTML);
    }
  };

  const deleteGridRow = (index) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editableContent, "text/html");
    const grids = doc.querySelectorAll('.dynamic-grid-row');
    if (grids[index]) {
        grids[index].remove();
        setEditableContent(doc.body.innerHTML);
    }
  };

  // --- PARSER LOGIC ---
  let inputCounter = 0;
  let dynamicTableCounter = -1;
  let dynamicGridCounter = -1;

  const options = {
    replace: (domNode) => {
        // 1. Tables (Add Button)
        if (domNode.name === 'table' && domNode.attribs && domNode.attribs.class?.includes('dynamic-result-table')) {
            dynamicTableCounter++;
            const currentTableIndex = dynamicTableCounter;
            return (
                <div className="group/table mb-6 relative" key={`tbl-${currentTableIndex}`}>
                    <table {...domNode.attribs}>
                        {domToReact(domNode.children, options)}
                    </table>
                    <button onClick={() => addTestRow(currentTableIndex)} className="flex items-center gap-1.5 text-[11px] bg-[#3f54e7]/10 text-[#3f54e7] px-3 py-1.5 rounded-full hover:bg-[#3f54e7] hover:text-white mt-2 print:hidden ml-auto font-bold opacity-0 group-hover/table:opacity-100 transition-all shadow-sm border border-[#3f54e7]/20">
                        <FaPlus size={9} /> Add
                    </button>
                </div>
            );
        }

        // 2. Rows (Delete Button)
        if (domNode.name === 'tr' && domNode.parent?.name === 'tbody' && domNode.parent?.parent?.attribs?.class?.includes('dynamic-result-table')) {
            const isHeader = domNode.attribs && domNode.attribs['data-is-header'] === 'true';
            const siblings = domNode.parent.children.filter(child => child.name === 'tr');
            const rowIndex = siblings.indexOf(domNode);
            const currentTableIndex = dynamicTableCounter;

            return (
                <tr {...domNode.attribs} className="group/row relative hover:bg-gray-50/50 transition-colors">
                    {domToReact(domNode.children, options)}
                    {!isHeader && (
                        <td className="w-6 border-none bg-transparent align-middle print:hidden">
                            <button onClick={() => deleteTestRow(currentTableIndex, rowIndex)} className="text-red-300 hover:text-red-600 opacity-0 group-hover/row:opacity-100 transition-opacity p-1.5">
                                <FaTrash size={12} />
                            </button>
                        </td>
                    )}
                </tr>
            );
        }

        // 3. Grid Rows
        if (domNode.name === 'div' && domNode.attribs?.class?.includes('dynamic-grid-row')) {
            dynamicGridCounter++;
            const currentGridIndex = dynamicGridCounter;
            return (
                <div className="group/grid relative flex items-center w-full hover:bg-gray-50 pr-8" key={`grid-${currentGridIndex}`}>
                    <div className="w-full">
                        <div {...domNode.attribs}>
                            {domToReact(domNode.children, options)}
                        </div>
                    </div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover/grid:opacity-100 transition-opacity print:hidden bg-white shadow-sm border rounded p-1">
                        <button onClick={() => addGridRow(currentGridIndex)} className="text-[#3f54e7] hover:bg-[#3f54e7]/10 p-1.5 rounded"><FaPlus size={10} /></button>
                        <button onClick={() => deleteGridRow(currentGridIndex)} className="text-red-400 hover:bg-red-50 p-1.5 rounded"><FaTrash size={10} /></button>
                    </div>
                </div>
            );
        }

        // 4. Inputs
        if (domNode.type === "text" && domNode.data) {
            const text = domNode.data;
            if (text.includes("{{Name}}")) return renderMinimalInput("name", "w-full font-bold text-gray-900");
            if (text.includes("{{Age}}")) return renderMinimalInput("age", "w-full");
            if (text.includes("{{Reg}}")) return renderMinimalInput("reg", "w-full");
            if (text.includes("{{Date}}")) return renderMinimalInput("date", "w-full");
            if (text.includes("{{Ref}}")) return renderMinimalInput("referredBy", "w-full font-bold text-gray-900");

            if (text.includes("__INPUT")) {
                const uniqueKey = `field_${inputCounter++}`;
                let defaultValue = "";
                if (text.includes(":") && text.includes("__")) {
                    const match = text.match(/__INPUT:(.*?)__/);
                    if (match && match[1]) defaultValue = match[1];
                }
                const val = formData[uniqueKey] !== undefined ? formData[uniqueKey] : defaultValue;
                return (
                    <input
                        key={uniqueKey}
                        type="text"
                        value={val}
                        onChange={(e) => handleInputChange(uniqueKey, e.target.value)}
                        className="w-full bg-transparent border-b border-dotted text-center border-gray-300 focus:border-[#3f54e7] outline-none px-1 text-inherit transition-colors min-w-[20px]"
                        placeholder="..."
                    />
                );
            }
        }
    }
  };

  const renderMinimalInput = (field, extraClass = "") => (
    <input
      value={formData[field] || ""}
      onChange={(e) => handleInputChange(field, e.target.value)}
      className={`bg-transparent border-none outline-none text-inherit h-full ${extraClass}`}
    />
  );

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex flex-col items-center w-full bg-white shadow-sm border border-gray-200 rounded-xl p-10">
      
      {/* TOOLBAR */}
      <div className="w-full max-w-[210mm] flex justify-between items-center mb-6 print:hidden">
        <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2">
            <span className="w-2 h-6 bg-[#3f54e7] rounded-full"></span>
            Lab Report Editor
        </h2>
        
        {/* 🔥 FIX: Use handleFinalSave */}
        <div className="flex gap-3">
          <button onClick={handleFinalSave} 
            className="flex items-center gap-2 bg-[#3f54e7] hover:bg-[#2c3eb5] text-white px-5 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all font-semibold text-sm">
            <FaSave size={14} /> Save Report
          </button>
          
          <button onClick={handlePrint} 
            className="flex items-center gap-2 bg-[#60b452] hover:bg-[#4a963e] text-white px-5 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all font-semibold text-sm">
            <FaPrint size={14} /> Print
          </button>
        </div>
      </div>

      {/* REPORT PAPER */}
      <div className="shadow-xl print:shadow-none" id="print-area">
        <div ref={componentRef} className="bg-white w-[210mm] min-h-[297mm] mx-auto relative flex flex-col justify-between">
            {/* HEADER */}
            <div className="h-[100px] flex items-center justify-between px-8 text-white header-gradient print-bg-force">
                <div className="logo-container">
                    <img src={hospitalLogoWhite} alt="Logo" className="h-auto w-48" />
                </div>
                <div className="text-right text-[9pt] leading-tight">
                    <p>+880 1601-666893</p>
                    <p>muktihospitalpvtlimited@gmail.com</p>
                    <p>https://muktihospital.com</p>
                </div>
            </div>

            {/* CONTENT */}
            <div className="flex-grow pt-8 px-[10mm] pb-4 text-[14px] leading-relaxed font-serif text-gray-800">
                <h1 className="mb-6 text-center text-xl font-bold text-[#323290] underline uppercase tracking-wider">
                    Pathology Report
                </h1>
                <div className="template-content">
                    {editableContent ? parse(editableContent, options) : <div className="flex items-center justify-center h-48 text-gray-400">Loading Template...</div>}
                </div>
            </div>

            {/* FOOTER */}
            <div className="w-full mt-auto">
                <div className="relative bg-[#e8f0ed] p-3 px-[10mm] print-bg-force h-[80px] overflow-hidden">
                    <div className="footer-shape print-bg-force"></div>
                    <div className="relative z-10 grid grid-cols-2 gap-x-5 gap-y-1 text-[8pt] text-gray-800 pt-1">
                        <p className="flex items-center gap-2"><FaPhoneAlt size={10} /> <span>+880 1601-666893</span></p>
                        <p className="flex items-center gap-2"><FaMapMarkerAlt size={10} /> <span>Race Course, Sasongacha, Cumilla</span></p>
                        <p className="flex items-center gap-2"><FaEnvelope size={10} /> <span>muktihospitalpvtlimited@gmail.com</span></p>
                        <p className="flex items-center gap-2"><FaGlobe size={10} /> <span>https://muktihospital.com</span></p>
                    </div>
                </div>
                <div className="flex items-center justify-between bg-gray-800 p-2 px-[10mm] text-[8pt] text-white print-bg-force">
                    <p>Software Powered By: Microters</p>
                    <div className="flex items-center gap-2"><FaGlobe size={10}/> https://microters.com <FaPhoneAlt size={10}/> +880 162-519-2766</div>
                </div>
            </div>
        </div>
      </div>

      <style>{`
        .header-gradient { background: linear-gradient(120deg, #3f54e7, #60b452) !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .footer-shape { content: ''; position: absolute; top: 0; right: 0; width: 150px; height: 100%; background-color: #60b452 !important; clip-path: polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%); -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .print-bg-force { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

        @media print { 
            @page { margin: 0; size: A4; }
            body * { visibility: hidden; }
            #print-area, #print-area * { visibility: visible; }
            #print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; background: white; }
            input::placeholder { color: transparent; }
            input { border-bottom: none !important; }
        }
      `}</style>
    </div>
  );
};

export default LabEditor;